import { Card, Stack, Table, type TableColumn } from "@knowtex-ai/components";
import type { McRun, McUsage, McUsageRow } from "../../lib/mc";
import { formatCost, formatCount, formatElapsed, formatUsd } from "./format";
import { GAP, SectionHeading } from "./layout";

const columnsFor = (keyHeader: string): TableColumn<McUsageRow>[] => [
  { id: "key", header: keyHeader, width: 200, grow: true, accessor: "key", sortable: true, cell: (r) => <span className="font-mono">{r.key}</span> },
  { id: "count", header: "Calls", width: 80, align: "end", accessor: "count", sortable: true, cell: (r) => formatCount(r.count) },
  { id: "input", header: "Input tokens", width: 130, align: "end", accessor: "inputTokens", sortable: true, cell: (r) => formatCount(r.inputTokens) },
  { id: "output", header: "Output tokens", width: 140, align: "end", accessor: "outputTokens", sortable: true, cell: (r) => formatCount(r.outputTokens) },
  { id: "cacheRead", header: "Cache read", width: 120, align: "end", accessor: "cacheReadTokens", sortable: true, cell: (r) => formatCount(r.cacheReadTokens) },
  { id: "cacheWrite", header: "Cache write", width: 120, align: "end", accessor: "cacheWriteTokens", sortable: true, cell: (r) => formatCount(r.cacheWriteTokens) },
  { id: "seconds", header: "Time", width: 90, align: "end", accessor: "seconds", sortable: true, cell: (r) => formatElapsed(r.seconds * 1000) },
  { id: "cost", header: "Cost", width: 90, align: "end", accessor: "costUsd", sortable: true, cell: (r) => formatUsd(r.costUsd) },
];

const SECTIONS: { id: keyof Omit<McUsage, "totalUsd">; title: string; columns: TableColumn<McUsageRow>[] }[] = [
  { id: "byKind", title: "By kind", columns: columnsFor("Kind") },
  { id: "byMission", title: "By mission", columns: columnsFor("Mission") },
  { id: "byModel", title: "By model", columns: columnsFor("Model") },
];

interface RunCostProps {
  run: McRun;
  usage: McUsage | undefined;
  loading: boolean;
}

export default function RunCost({ run, usage, loading }: RunCostProps) {
  const total = formatCost(run);
  return (
    <Card variant="elevated" title="Cost" subtitle={total === "—" ? undefined : `${total} total`}>
      <Stack gap={GAP.section}>
        {SECTIONS.map((section) => (
          <section key={section.id}>
            <SectionHeading>{section.title}</SectionHeading>
            <Table
              aria-label={`Cost ${section.title.toLowerCase()}`}
              size="small"
              columns={section.columns}
              data={usage?.[section.id] ?? []}
              getRowId={(row) => row.key}
              loading={loading}
              defaultSortState={{ columnId: "cost", direction: "desc" }}
              paginated={false}
              emptyState="No usage recorded."
            />
          </section>
        ))}
      </Stack>
    </Card>
  );
}
